import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    DataList,
    MessageBanner,
    CustomProgressBar,
    MessageGuide,
    PreviewData,
    UploadModal,
    ConcurrentUploadModal,
    MessageModal
} from 'common/components';
import {
    FILE_PREVIEW_PROGRESS,
    FILE_UPLOAD_SUCCESS_MESSAGE,
    SEARCH_DEBOUNCE_DELAY,
    MAIN_ROUTES,
    ERROR_FIX_COMPLETED_MESSAGE,
    ERROR_FIX_PENDING_MESSAGE,
    DATA_EDITOR_SYNC_PROGRESS,
    CLEAR_PREVIEW_CONF_TITLE,
    CLEAR_PREVIEW_CONF_MESSAGE
} from 'common/config';
import {
    ApiStatus,
    ButtonLabel,
    EntityActions,
    MessageType,
    Schema,
    UploadStatus
} from 'common/enums';
import useDebounce from 'common/hooks/useDebounce';
import { formatDatetime } from 'common/utils/file';
import { RootState } from 'common/redux/core/root.reducer';
import { getMessageModalDetails } from 'common/redux/app-state/appState.selector';
import { fileUploadActions } from 'common/redux/file-upload';
import {
    getUploadModalVisibility,
    getFileUploadResponseStatus,
    getUploadStatus,
    getSyncDetails
} from 'common/redux/file-upload/fileUpload.selector';
import {
    getErrorCount,
    getErrorFixedStatus,
    getPreviewData
} from 'common/redux/preview-data/previewData.selector';
import { getUserEmail } from 'common/redux/user/user.selector';
import { previewDataActions } from 'common/redux/preview-data';

import Header from 'modules/data-editor/components/header/Header';
import { dataEditorActions } from 'modules/data-editor/redux';
import {
    getMode,
    getMessageGuide,
    getMessageBanner,
    getLoadToMasterStatus,
    getClearPreviewStatus
} from 'modules/data-editor/redux/dataEditor.selector';
import { Mode } from 'modules/data-editor/enums/mode.enum';
import { getDataListColDefs } from 'modules/data-editor/config';

import './DataEditor.scss';
import useDataEditorAccess from 'modules/data-editor/role-access-hooks/useDataEditorAccess';

const DataEditor: FC = () => {
    const [searchVal, setSearchVal] = useState<string>('');
    const [confModalDetails, setConfModalDetails] = useState({ title: '', text: '', type: '' });
    const [isConfModalVisible, setIsConfModalVisible] = useState<boolean>(false);
    const { isDataEditorUploadDisabled, isDataEditorPreviewDisabled, isMapToMasterDataDisabled } =
        useDataEditorAccess();

    const isUploadModalVisible = useSelector((state: RootState) => getUploadModalVisibility(state));
    const uploadResponseStatus = useSelector((state: RootState) =>
        getFileUploadResponseStatus(state)
    );
    const mode = useSelector((state: RootState) => getMode(state));
    const errorCount = useSelector((state: RootState) => getErrorCount(state));
    const isAtleastOneErrorFixed = useSelector((state: RootState) => getErrorFixedStatus(state));
    const messageGuide = useSelector((state: RootState) => getMessageGuide(state));
    const messageBanner = useSelector((state: RootState) => getMessageBanner(state));
    const messageModal = useSelector((state: RootState) => getMessageModalDetails(state));
    const uploadStatus = useSelector((state: RootState) => getUploadStatus(state));
    const userEmail = useSelector((state: RootState) => getUserEmail(state));
    const previewData = useSelector((state: RootState) => getPreviewData(state));
    const loadToMasterStatus = useSelector((state: RootState) => getLoadToMasterStatus(state));
    const fileSyncStatus = useSelector((state: RootState) => getSyncDetails(state));
    const clearPreviewStatus = useSelector((state: RootState) => getClearPreviewStatus(state));

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const setMode = useCallback(() => {
        if (uploadStatus.status === ApiStatus.SUCCESS) {
            if (
                uploadStatus.data?.fileDetails?.uploadedUser?.emailAddress === '' ||
                (uploadStatus.data?.fileDetails?.uploadedUser?.emailAddress &&
                    uploadStatus.data.fileDetails.uploadedUser.emailAddress.toLowerCase() !==
                        userEmail.toLowerCase())
            ) {
                dispatch(dataEditorActions.setMode(Mode.Concurrent));
            } else if (uploadStatus?.data?.previewStatus === UploadStatus.InProgress) {
                dispatch(dataEditorActions.setMode(Mode.PreviewGeneration));
            } else if (uploadStatus?.data?.previewStatus === UploadStatus.Open) {
                dispatch(dataEditorActions.setMode(Mode.Initial));
            } else if (uploadStatus?.data?.previewStatus === UploadStatus.Validation) {
                dispatch(fileUploadActions.getSyncDetails());
            }
        }
    }, [
        dispatch,
        uploadStatus.data?.fileDetails?.uploadedUser.emailAddress,
        uploadStatus.data?.previewStatus,
        uploadStatus.status,
        userEmail
    ]);

    useEffect(() => {
        dispatch(fileUploadActions.fetchUploadStatus());
        dispatch(previewDataActions.resetToInitial());
    }, [dispatch]);

    useEffect(() => {
        if (
            uploadStatus?.data?.previewStatus === UploadStatus.Validation &&
            fileSyncStatus?.previewStatus === UploadStatus.InProgress &&
            fileSyncStatus?.fileDetails?.sourceSchema === Schema.Preview
        ) {
            dispatch(dataEditorActions.setMode(Mode.DataEditorSync));
        } else if (uploadStatus?.data?.previewStatus === UploadStatus.Validation) {
            dispatch(dataEditorActions.setMode(Mode.DataLoaded));
        }
    }, [
        dispatch,
        fileSyncStatus?.fileDetails?.sourceSchema,
        fileSyncStatus?.previewStatus,
        uploadStatus?.data?.previewStatus
    ]);

    useEffect(() => {
        if (uploadResponseStatus === ApiStatus.SUCCESS) {
            dispatch(dataEditorActions.setMode(Mode.PreviewGeneration));
            dispatch(fileUploadActions.resetUploadResponse());
            dispatch(
                dataEditorActions.setMessageBanner({
                    isVisible: true,
                    message: FILE_UPLOAD_SUCCESS_MESSAGE
                })
            );
            setTimeout(() => {
                dispatch(dataEditorActions.setMessageBanner({ isVisible: false, message: '' }));
            }, 10000);
        }
    }, [dispatch, uploadResponseStatus]);

    useEffect(() => {
        setMode();
    }, [dispatch, setMode, uploadStatus, userEmail]);

    useEffect(() => {
        if (mode === Mode.DataSavedToMaster && loadToMasterStatus === ApiStatus.SUCCESS) {
            if (!messageModal?.isVisible) {
                navigate(MAIN_ROUTES.MASTER_DATA);
                dispatch(dataEditorActions.resetState());
            }
        } else if (mode === Mode.DataSavedToMaster && loadToMasterStatus === ApiStatus.ERROR) {
            setMode();
        }
    }, [dispatch, loadToMasterStatus, messageModal?.isVisible, mode, navigate, setMode]);

    useEffect(() => {
        if (errorCount > 0 && messageBanner.isVisible === false) {
            dispatch(
                dataEditorActions.setMessageGuide({
                    isVisible: true,
                    message: ERROR_FIX_PENDING_MESSAGE
                })
            );
        }
    }, [dispatch, errorCount, messageBanner.isVisible]);

    useEffect(() => {
        if (
            errorCount === 0 &&
            isAtleastOneErrorFixed &&
            previewData?.data?.data?.length &&
            previewData?.data?.data?.length > 0
        ) {
            dispatch(dataEditorActions.setMessageGuide({ isVisible: false, message: '' }));
            dispatch(
                dataEditorActions.setMessageBanner({
                    isVisible: true,
                    message: ERROR_FIX_COMPLETED_MESSAGE
                })
            );
        }
    }, [dispatch, errorCount, isAtleastOneErrorFixed, previewData?.data?.data?.length]);

    useEffect(() => {
        if (mode === Mode.PreviewCleared && clearPreviewStatus === ApiStatus.SUCCESS) {
            if (!messageModal?.isVisible) {
                window.location.reload();
            }
        } else if (mode === Mode.PreviewCleared && clearPreviewStatus === ApiStatus.ERROR) {
            setMode();
            dispatch(dataEditorActions.resetClearPreview());
        }
    }, [clearPreviewStatus, dispatch, messageModal?.isVisible, mode, setMode]);

    const onSearchChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchVal(event.target.value);
    };

    const onSearch = () => {
        return;
    };

    useDebounce(
        () => {
            setSearchVal(searchVal);
        },
        [searchVal],
        SEARCH_DEBOUNCE_DELAY
    );

    const handleUploadToggle = () => {
        dispatch(fileUploadActions.setVisibility(!isUploadModalVisible));
        dispatch(fileUploadActions.setSelectedFiles([]));
    };

    const handleHeaderPrimaryAction = (action: EntityActions) => () => {
        if (action === EntityActions.Upload) {
            handleUploadToggle();
        } else if (action === EntityActions.LoadToMaster) {
            dispatch(dataEditorActions.setMode(Mode.DataSavedToMaster));
            dispatch(dataEditorActions.loadToMasterData());
        }
    };

    const handleHeaderSecondaryAction = (action: EntityActions) => () => {
        if (action === EntityActions.ClearPreview) {
            setIsConfModalVisible(true);
            setConfModalDetails({
                title: CLEAR_PREVIEW_CONF_TITLE,
                text: CLEAR_PREVIEW_CONF_MESSAGE,
                type: EntityActions.ClearPreview
            });
        }
    };

    const handleMessageBannerClose = () => {
        dispatch(dataEditorActions.setMessageBanner({ isVisible: false, message: '' }));
    };

    const handleConfPrimaryAction = () => {
        setIsConfModalVisible(false);
        if (confModalDetails.type === EntityActions.ClearPreview) {
            dispatch(dataEditorActions.setMode(Mode.PreviewCleared));
            dispatch(dataEditorActions.clearPreview());
        }
    };

    const handleConfSecondaryAction = () => {
        setIsConfModalVisible(false);
        setConfModalDetails({ title: '', text: '', type: '' });
    };

    const getUploadedUserName = () => {
        return `${uploadStatus.data?.fileDetails?.uploadedUser?.firstName || ''} ${
            uploadStatus.data?.fileDetails?.uploadedUser?.lastName || ''
        }`;
    };

    const isHeaderButtonDisabled = mode === Mode.DataLoaded && errorCount > 0;
    const isHeaderRightHidden =
        mode === Mode.PreviewGeneration || mode === Mode.Concurrent || mode === Mode.DataEditorSync;

    return (
        <div className="data-editor-container">
            <Header
                dataEditorMode={mode}
                isButtonDisabled={isHeaderButtonDisabled}
                searchVal={searchVal}
                isRightSectionHidden={isHeaderRightHidden}
                isDataEditorUploadDisabled={isDataEditorUploadDisabled}
                isDataEditorPreviewDisabled={isDataEditorPreviewDisabled}
                isMapToMasterDataDisabled={isMapToMasterDataDisabled}
                onPrimaryAction={handleHeaderPrimaryAction}
                onSearchChangeHandler={onSearchChangeHandler}
                onSearch={onSearch}
                onSecondaryAction={handleHeaderSecondaryAction}
            />
            {messageBanner.isVisible && (
                <div className="data-editor-message-banner">
                    <MessageBanner
                        message={messageBanner.message}
                        onClose={handleMessageBannerClose}
                    />
                </div>
            )}
            {messageGuide.isVisible && searchVal.length < 3 && (
                <MessageGuide message={messageGuide.message} />
            )}
            <div
                className={`data-list-wrapper ${
                    searchVal.length >= 3
                        ? 'search-height'
                        : mode === Mode.DataLoaded
                        ? messageBanner.isVisible || messageGuide.isVisible
                            ? 'small-height'
                            : 'medium-height'
                        : 'full-height'
                }`}>
                {mode === Mode.DataLoaded ? (
                    <PreviewData
                        searchVal={searchVal}
                        isDataEditorPreviewDisabled={isDataEditorPreviewDisabled}
                    />
                ) : (
                    <DataList
                        isNoDataVisible={
                            uploadStatus?.status === ApiStatus.SUCCESS && mode === Mode.Initial
                        }
                        data={[]}
                        columnDefs={getDataListColDefs(
                            () => {
                                return;
                            },
                            () => {
                                return;
                            },
                            () => {
                                return;
                            },
                            0,
                            false
                        )}
                    />
                )}
            </div>
            <UploadModal isVisible={isUploadModalVisible} targetId="" />
            {mode === Mode.PreviewGeneration && (
                <CustomProgressBar isIndeterminate labelComponent={FILE_PREVIEW_PROGRESS} />
            )}
            {mode === Mode.DataEditorSync && (
                <CustomProgressBar isIndeterminate labelComponent={DATA_EDITOR_SYNC_PROGRESS} />
            )}
            {mode === Mode.Concurrent && uploadStatus.data && (
                <ConcurrentUploadModal
                    userName={getUploadedUserName()}
                    activeSection="Data Editor"
                    uploadedOn={formatDatetime(
                        uploadStatus.data?.fileDetails?.uploadedOn.toString()
                    )}
                />
            )}
            {isConfModalVisible && (
                <MessageModal
                    headerName={confModalDetails.title}
                    message={confModalDetails.text}
                    isVisible={isConfModalVisible}
                    isSecondaryButtonVisible={true}
                    type={MessageType.Warning}
                    primaryButton={ButtonLabel.Ok}
                    secondaryButton={ButtonLabel.Cancel}
                    onPrimaryAction={handleConfPrimaryAction}
                    onSecondaryAction={handleConfSecondaryAction}
                />
            )}
        </div>
    );
};

export default DataEditor;
